import {
  hasNameLetter,
  isValidNameCharset,
  NAME_ALLOWED_CHARACTERS,
  NAME_ALLOWED_CHARACTERS_ERROR_MESSAGE,
  NAME_MUST_CONTAIN_LETTER_ERROR_MESSAGE,
  THAI_TITLES,
} from '@shared/utils';
import { z } from 'zod';

import {
  CAUSE_ALLOW_CHARACTERS,
  CAUSE_ALLOW_CHARACTERS_ERROR_MESSAGE,
  DocumentTypes,
} from '../constants';

const MAX_FIRSTNAME_LENGTH = 256;
const MAX_MIDDLENAME_LENGTH = 256;
const MAX_LASTNAME_LENGTH = 256;
const MAX_CAUSE_LENGTH = 1024;

// NOTE: null is used as empty value for name title so API can receive title: null

// NOTE: This is shared schema for desktop/mobile alternative registration container
export const AlternativeRegistrationSchema = z.object({
  nameTitle: z
    .enum(THAI_TITLES)
    .nullable()
    .refine((arg) => arg !== null, {
      message: 'กรุณาเลือกคำนำหน้า',
    }),
  alternativeNameTitle: z.string().nullish(),
  firstName: z
    .string()
    .trim()
    .min(1, 'กรุณากรอกชื่อ')
    .max(MAX_FIRSTNAME_LENGTH, `ชื่อต้องมีความยาวไม่เกิน ${MAX_FIRSTNAME_LENGTH} ตัวอักษร`)
    .regex(NAME_ALLOWED_CHARACTERS, { message: NAME_ALLOWED_CHARACTERS_ERROR_MESSAGE })
    .refine(hasNameLetter, { message: NAME_MUST_CONTAIN_LETTER_ERROR_MESSAGE }),
  // Middle name is optional, so '' passes both checks. A punctuation-only value such as '-'
  // does not — upstream systems send it to mean "no middle name". Both checks run after
  // .trim(), so '  -  ' is caught too. The charset and letter rules are checked separately
  // because they have different error copy.
  middleName: z
    .string()
    .trim()
    .max(MAX_MIDDLENAME_LENGTH, `ชื่อกลางต้องมีความยาวไม่เกิน ${MAX_MIDDLENAME_LENGTH} ตัวอักษร`)
    .refine((value) => value === '' || isValidNameCharset(value), {
      message: NAME_ALLOWED_CHARACTERS_ERROR_MESSAGE,
    })
    .refine((value) => value === '' || hasNameLetter(value), {
      message: NAME_MUST_CONTAIN_LETTER_ERROR_MESSAGE,
    })
    .optional(),
  lastName: z
    .string()
    .trim()
    .min(1, 'กรุณากรอกนามสกุล')
    .max(MAX_LASTNAME_LENGTH, `นามสกุลต้องมีความยาวไม่เกิน ${MAX_LASTNAME_LENGTH} ตัวอักษร`)
    .regex(NAME_ALLOWED_CHARACTERS, { message: NAME_ALLOWED_CHARACTERS_ERROR_MESSAGE })
    .refine(hasNameLetter, { message: NAME_MUST_CONTAIN_LETTER_ERROR_MESSAGE }),
  documentType: z
    .enum(DocumentTypes)
    .or(z.literal(''))
    .refine((arg) => arg !== '', {
      message: 'กรุณาเลือกประเภทเอกสาร',
    }),
  documentImage: z.string().min(1, { message: 'กรุณาถ่ายภาพเอกสาร' }),
  cause: z
    .string()
    .trim()
    .min(1, 'กรุณากรอกสาเหตุ')
    .max(MAX_CAUSE_LENGTH, `สาเหตุต้องมีความยาวไม่เกิน ${MAX_CAUSE_LENGTH} ตัวอักษร`)
    .regex(CAUSE_ALLOW_CHARACTERS, {
      message: CAUSE_ALLOW_CHARACTERS_ERROR_MESSAGE,
    })
    .refine((arg) => arg.trim() !== '', { message: 'กรุณากรอกสาเหตุ' }),
});

export type AlternativeRegistrationFormInput = z.input<typeof AlternativeRegistrationSchema>;
export type AlternativeRegistrationFormOutput = z.output<typeof AlternativeRegistrationSchema>;
